import { useState, useEffect } from "react";
import { GoogleLogo, MicrosoftLogo, YahooLogo } from "./BrandLogos";
import wordmarkLockup from "./sr-wordmark-lockup.png";
import { C, FONT } from "./ui/theme";
import { TextField, PasswordField, Checkbox, ValidationMessage, Spinner, FormKitStyles, Label } from "./ui/FormKit";
import heroPhoto from "./assets/hero-team-success.jpg";

// Authentication-surface styling — light mode only, restrained, and
// intentionally distinct from the marketing pill buttons (ui/Button.js):
// a ~12px rounded-rect radius matching the form fields above it, not a
// full pill, per this page's "not an oversized pill" requirement. All
// hover/active/disabled/focus states are plain CSS so there's no flicker
// on first paint and no per-instance JS state to keep in sync.
const authStyles = `
  /* Same background photo + same white-glass wash used on the landing
     page, covering the full screen — applied to html/body too (not just
     .auth-page) so mobile's overscroll bounce doesn't reveal a plain
     white gap, exactly like the fix made on the landing page. */
  html, body {
    min-height: 100%;
    min-height: 100dvh;
    background-image: url(${heroPhoto});
    background-size: cover;
    background-position: center 35%;
    background-repeat: no-repeat;
    background-color: ${C.bgAlt};
    background-attachment: fixed;
  }
  @media (max-width: 768px) {
    html, body { background-attachment: scroll; }
  }
  .auth-page {
    position: relative;
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    background-image: url(${heroPhoto});
    background-size: cover;
    background-position: center 35%;
    background-repeat: no-repeat;
    background-attachment: fixed;
  }
  @media (max-width: 768px) {
    .auth-page { background-attachment: scroll; }
  }
  .auth-page-glow {
    position: absolute; inset: 0;
    background: rgba(255,255,255,.6);
    backdrop-filter: blur(14px) saturate(1.3);
    -webkit-backdrop-filter: blur(14px) saturate(1.3);
  }
  .auth-top, .auth-main { position: relative; z-index: 1; }
  .auth-top { padding: 20px 6%; }
  .auth-main { flex: 1; display: flex; align-items: center; justify-content: center; padding: 24px 20px 72px; }
  .auth-card { padding: 40px 36px; }

  .auth-logo-btn { font-family: ${FONT}; background: none; border: none; padding: 0; cursor: pointer; display: inline-flex; align-items: center; gap: 9px; border-radius: 6px; }
  .auth-logo-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 4px; }

  .auth-btn { font-family: ${FONT}; font-weight: 600; font-size: 15px; border: none; border-radius: 12px; cursor: pointer; width: 100%; padding: 13.5px 0; display: inline-flex; align-items: center; justify-content: center; gap: 8px; transition: background .15s ease, transform .1s ease, opacity .15s ease; }
  .auth-btn-primary { background: ${C.accent}; color: #fff; }
  .auth-btn-primary:hover:not(:disabled) { background: ${C.accentHover}; }
  .auth-btn-primary:active:not(:disabled) { transform: scale(.985); }
  .auth-btn:disabled { opacity: .55; cursor: default; }
  .auth-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }

  .oauth-btn { font-family: ${FONT}; font-weight: 500; font-size: 14.5px; width: 100%; height: 48px; display: flex; align-items: center; justify-content: center; gap: 10px; background: #fff; color: ${C.text}; border: 1px solid ${C.border}; border-radius: 12px; cursor: pointer; transition: background .15s ease, border-color .15s ease, transform .1s ease; }
  .oauth-btn:hover { background: ${C.bgAlt}; border-color: ${C.textFaint}; }
  .oauth-btn:active { transform: scale(.99); }
  .oauth-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }

  .auth-link-btn { font-family: ${FONT}; background: none; border: none; padding: 0; color: ${C.accent}; font-size: 13.5px; font-weight: 600; cursor: pointer; }
  .auth-link-btn:hover { text-decoration: underline; }
  .auth-link-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; border-radius: 4px; }

  .auth-back-btn { font-family: ${FONT}; background: none; border: none; padding: 0; color: ${C.textSub}; font-size: 13.5px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
  .auth-back-btn:hover { color: ${C.text}; }
  .auth-back-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; border-radius: 4px; }

  .auth-select, .auth-plain-input { font-family: ${FONT}; font-size: 15px; color: ${C.text}; background: #fff; border: 1px solid ${C.border}; border-radius: 12px; padding: 13px 14px; outline: none; box-sizing: border-box; width: 100%; transition: border-color .15s ease, box-shadow .15s ease; }
  .auth-select:focus, .auth-plain-input:focus { border-color: ${C.accent}; box-shadow: 0 0 0 4px rgba(0,113,227,0.12); }
  .auth-select { cursor: pointer; }

  .auth-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 380px) { .auth-grid-2 { grid-template-columns: 1fr; } }

  @media (max-width: 480px) {
    .auth-card { padding: 32px 22px; border-radius: 16px !important; }
    .auth-main { padding: 12px 16px 56px; }
  }
`;

// Same account either way this button is clicked — the provider tells us
// who you are, and the backend logs you into a matching account or creates
// a new one on the spot if it's the first time we've seen that email. This
// consent screen ALSO requests mail send/read access (not just identity),
// so this same login doubles as connecting a working, primary send/receive
// mailbox — see server.js's linkLoginAccountAsMailbox(). That's why the
// explanatory copy below spells out that it asks for more than identity.
function OAuthButton({ provider, label, icon }) {
  return (
    <button type="button" className="oauth-btn" onClick={() => { window.location.href = "/api/auth/oauth/" + provider + "/start"; }}>
      {icon}<span>{label}</span>
    </button>
  );
}

function OAuthButtons() {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
        <OAuthButton provider="google" label="Continue with Google" icon={<GoogleLogo size={18} />} />
        <OAuthButton provider="microsoft" label="Continue with Microsoft" icon={<MicrosoftLogo size={18} />} />
        <OAuthButton provider="yahoo" label="Continue with Yahoo" icon={<YahooLogo size={18} />} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 22 }}>
        <div style={{ flex: 1, height: 1, background: C.borderSoft }} />
        <span style={{ fontSize: 11.5, color: C.textFaint, fontWeight: 600, letterSpacing: ".3px" }}>OR</span>
        <div style={{ flex: 1, height: 1, background: C.borderSoft }} />
      </div>
    </div>
  );
}

// Primary/secondary action button for this page only — rounded-rect (not
// full pill), consistent height and radius with the fields above it.
function AuthButton({ children, loading, disabled, style, ...props }) {
  return (
    <button type="button" className="auth-btn auth-btn-primary" disabled={disabled || loading} aria-busy={loading || undefined} style={style} {...props}>
      {loading && <Spinner size={15} color="#fff" />}
      {children}
    </button>
  );
}

const COUNTRIES = [
  { code: "+1", name: "US" }, { code: "+44", name: "UK" },
  { code: "+91", name: "IN" }, { code: "+61", name: "AU" },
  { code: "+49", name: "DE" }, { code: "+33", name: "FR" },
  { code: "+81", name: "JP" }, { code: "+86", name: "CN" },
  { code: "+55", name: "BR" }, { code: "+27", name: "ZA" },
  { code: "+971", name: "AE" }, { code: "+65", name: "SG" },
  { code: "+60", name: "MY" }, { code: "+234", name: "NG" },
  { code: "+966", name: "SA" }, { code: "+39", name: "IT" },
  { code: "+34", name: "ES" }, { code: "+31", name: "NL" },
  { code: "+46", name: "SE" }, { code: "+7", name: "RU" },
  { code: "+82", name: "KR" }, { code: "+52", name: "MX" },
  { code: "+54", name: "AR" }, { code: "+92", name: "PK" },
  { code: "+880", name: "BD" }, { code: "+20", name: "EG" },
];

function PasswordStrength({ password }) {
  if (!password) return null;
  const checks = [
    { label: "8+ characters", ok: password.length >= 8 },
    { label: "Uppercase letter", ok: /[A-Z]/.test(password) },
    { label: "Number", ok: /[0-9]/.test(password) },
    { label: "Special character", ok: /[^A-Za-z0-9]/.test(password) },
  ];
  const score = checks.filter(c => c.ok).length;
  const barColor = score <= 1 ? C.red : score <= 3 ? C.amber : C.green;
  return (
    <div style={{ marginTop: -8 }}>
      <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= score ? barColor : C.borderSoft, transition: "background .2s ease" }} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {checks.map(c => (
          <span key={c.label} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: c.ok ? C.green : C.textFaint, fontWeight: c.ok ? 600 : 400 }}>
            <span aria-hidden style={{ fontSize: 10 }}>{c.ok ? "✓" : "•"}</span>{c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

const OAUTH_ERROR_MESSAGES = {
  google: "Google sign-in didn't go through — try again, or use your email and password below.",
  microsoft: "Microsoft sign-in didn't go through — try again, or use your email and password below.",
  yahoo: "Yahoo sign-in didn't go through — try again, or use your email and password below.",
  google_not_configured: "Google sign-in isn't set up yet — use your email and password below.",
  microsoft_not_configured: "Microsoft sign-in isn't set up yet — use your email and password below.",
  yahoo_not_configured: "Yahoo sign-in isn't set up yet — use your email and password below.",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Distinguishes a genuine network/connectivity failure (fetch never
// reached the server — offline, DNS, CORS, server down) from a normal
// API error response, so "network failure" gets its own honest message
// instead of a raw "Failed to fetch".
function friendlyError(e) {
  if (e instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(e?.message || "")) {
    return "Can't reach the server right now — check your connection and try again.";
  }
  return e?.message || "Something went wrong. Please try again.";
}

export default function LoginPage({ onLogin, initialError }) {
  const [view, setView] = useState("login");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState(() => initialError ? (OAUTH_ERROR_MESSAGES[initialError] || "Sign-in didn't go through — please try again.") : "");
  useEffect(() => { if (initialError) setErrorMsg(OAUTH_ERROR_MESSAGES[initialError] || "Sign-in didn't go through — please try again."); }, [initialError]);
  const [rememberMe, setRememberMe] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [countryCode, setCountryCode] = useState("+1");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ firstName: "", lastName: "", email: "", username: "", password: "", confirmPassword: "", company: "", jobTitle: "", mobile: "" });
  const [forgotEmail, setForgotEmail] = useState("");

  const lf = k => e => setLoginForm(p => ({ ...p, [k]: e.target.value }));
  const sf = k => e => setSignupForm(p => ({ ...p, [k]: e.target.value }));

  const resetMessages = () => { setErrors({}); setErrorMsg(""); setSuccessMsg(""); };
  const switchTo = (v) => { setView(v); resetMessages(); };

  const validateLogin = () => {
    const e = {};
    if (!loginForm.email) e.email = "Enter your email or username";
    if (!loginForm.password) e.password = "Enter your password";
    setErrors(e);
    return !Object.keys(e).length;
  };
  const validateSignup = () => {
    const e = {};
    if (!signupForm.firstName) e.firstName = "Required";
    if (!signupForm.lastName) e.lastName = "Required";
    if (!signupForm.email) e.email = "Required";
    else if (!EMAIL_RE.test(signupForm.email)) e.email = "Enter a valid email address";
    if (!signupForm.username || signupForm.username.length < 3) e.username = "Minimum 3 characters";
    if (!signupForm.password || signupForm.password.length < 8) e.password = "Minimum 8 characters";
    else if (signupForm.password !== signupForm.confirmPassword) e.confirmPassword = "Passwords don't match";
    if (!agreed) e.agreed = "You must agree to the terms to continue";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const doLogin = async () => {
    if (!validateLogin()) return;
    setLoading(true); setErrorMsg("");
    try {
      const r = await fetch("/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: loginForm.email, password: loginForm.password }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "That email/username and password didn't match. Please try again.");
      localStorage.setItem("sdr_token", d.token);
      if (rememberMe) localStorage.setItem("sdr_remember", "true");
      onLogin(d.user, d.token);
    } catch (e) {
      setErrorMsg(friendlyError(e));
    }
    setLoading(false);
  };

  const doSignup = async () => {
    if (!validateSignup()) return;
    setLoading(true); setErrorMsg("");
    try {
      const r = await fetch("/api/auth/signup", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...signupForm, mobile: signupForm.mobile ? countryCode + " " + signupForm.mobile : "" }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Couldn't create your account. Please try again.");
      localStorage.removeItem("sdr_plan");
      setView("login");
      setErrors({}); setErrorMsg("");
      setSuccessMsg("Account created — please sign in.");
    } catch (e) {
      setErrorMsg(friendlyError(e));
    }
    setLoading(false);
  };

  const doForgot = async () => {
    if (!forgotEmail) { setErrors({ forgot: "Enter your email address" }); return; }
    if (!EMAIL_RE.test(forgotEmail)) { setErrors({ forgot: "Enter a valid email address" }); return; }
    setErrors({}); setErrorMsg(""); setLoading(true);
    try {
      const r = await fetch("/api/auth/forgot-password", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: forgotEmail }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Couldn't send the reset link. Please try again.");
      setSuccessMsg("Reset link sent — check your email.");
    } catch (e) {
      setErrorMsg(friendlyError(e));
    }
    setLoading(false);
  };

  return (
    <div className="auth-page" style={{ fontFamily: FONT, color: C.text, WebkitFontSmoothing: "antialiased" }}>
      <style>{`*{box-sizing:border-box} ${authStyles}`}</style>
      <FormKitStyles />
      <div className="auth-page-glow" />

      {/* TOP — brand identity only; every other action (sign in / create
          account / forgot password) already lives inside the form below,
          so there's nothing else essential to duplicate up here. */}
      <div className="auth-top">
        <button type="button" className="auth-logo-btn" onClick={() => { window.location.href = "/"; }} aria-label="SDR ReachOut home">
          <img src={wordmarkLockup} alt="SDR ReachOut" style={{ height: 48, width: "auto", display: "block" }} />
        </button>
      </div>

      {/* MAIN */}
      <div className="auth-main">
        <div style={{ width: "100%", maxWidth: 408 }}>
          <p style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-.2px", textAlign: "center", margin: "0 0 16px", background: "linear-gradient(135deg,#0084FF,#6A28EF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>SDR ReachOut Platform</p>
          <div className="auth-card" style={{ background: "#fff", border: `1px solid ${C.borderSoft}`, borderRadius: 20, boxShadow: "0 1px 2px rgba(0,0,0,.03), 0 10px 28px rgba(0,0,0,.05)" }}>

            {successMsg && (
              <div role="status" style={{ display: "flex", gap: 8, alignItems: "flex-start", background: C.greenSoft, border: `1px solid ${C.green}33`, borderRadius: 12, padding: "12px 14px", marginBottom: 20, fontSize: 13.5, color: C.green, lineHeight: 1.5 }}>
                <span aria-hidden>✓</span><span>{successMsg}</span>
              </div>
            )}
            {errorMsg && (
              <div role="alert" style={{ display: "flex", gap: 8, alignItems: "flex-start", background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: 12, padding: "12px 14px", marginBottom: 20, fontSize: 13.5, color: C.red, lineHeight: 1.5 }}>
                <span aria-hidden>⚠</span><span>{errorMsg}</span>
              </div>
            )}

            {/* LOGIN */}
            {view === "login" && (
              <>
                <h1 style={{ fontSize: 24, fontWeight: 700, color: C.text, letterSpacing: "-.5px", marginBottom: 6, textAlign: "center" }}>Welcome back</h1>
                <p style={{ fontSize: 14.5, color: C.textSub, marginBottom: 28, textAlign: "center", lineHeight: 1.5 }}>Sign in to your SDR ReachOut workspace</p>

                <OAuthButtons />

                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <TextField id="login-email" label="Email or Username" value={loginForm.email} onChange={lf("email")} placeholder="you@company.com" error={errors.email} autoComplete="username" />
                  <PasswordField id="login-password" label="Password" value={loginForm.password} onChange={lf("password")} placeholder="Enter your password" error={errors.password} autoComplete="current-password" />
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "6px 0 24px" }}>
                  <Checkbox id="remember-me" label="Remember me" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} />
                  <button type="button" className="auth-link-btn" onClick={() => switchTo("forgot")}>Forgot password?</button>
                </div>

                <AuthButton onClick={doLogin} loading={loading} disabled={loading}>
                  {loading ? "Signing in…" : "Sign In"}
                </AuthButton>

                <p style={{ textAlign: "center", fontSize: 13.5, color: C.textSub, marginTop: 22 }}>
                  Don't have an account?{" "}
                  <button type="button" className="auth-link-btn" onClick={() => switchTo("signup")}>Create one</button>
                </p>
              </>
            )}

            {/* SIGNUP */}
            {view === "signup" && (
              <>
                <h1 style={{ fontSize: 24, fontWeight: 700, color: C.text, letterSpacing: "-.5px", marginBottom: 6, textAlign: "center" }}>Create your account</h1>
                <p style={{ fontSize: 14.5, color: C.textSub, marginBottom: 28, textAlign: "center", lineHeight: 1.5 }}>Join the SDR ReachOut platform</p>

                <OAuthButtons />

                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div className="auth-grid-2">
                    <TextField id="firstName" label="First Name" value={signupForm.firstName} onChange={sf("firstName")} placeholder="John" error={errors.firstName} autoComplete="given-name" />
                    <TextField id="lastName" label="Last Name" value={signupForm.lastName} onChange={sf("lastName")} placeholder="Smith" error={errors.lastName} autoComplete="family-name" />
                  </div>
                  <TextField id="signup-email" label="Email" type="email" value={signupForm.email} onChange={sf("email")} placeholder="you@company.com" error={errors.email} autoComplete="email" />
                  <TextField id="username" label="Username" value={signupForm.username} onChange={sf("username")} placeholder="john.smith" error={errors.username} autoComplete="username" />
                  <TextField id="company" label="Company" optional value={signupForm.company} onChange={sf("company")} placeholder="Acme Inc." autoComplete="organization" />

                  <div>
                    <Label htmlFor="mobile" optional>Mobile</Label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <select id="country-code" aria-label="Country code" className="auth-select" value={countryCode} onChange={e => setCountryCode(e.target.value)} style={{ width: 96, flexShrink: 0 }}>
                        {COUNTRIES.map(c => <option key={c.code + c.name} value={c.code}>{c.code} {c.name}</option>)}
                      </select>
                      <input id="mobile" className="auth-plain-input" value={signupForm.mobile} onChange={sf("mobile")} placeholder="234 567 8900" autoComplete="tel-national" />
                    </div>
                  </div>

                  <div>
                    <PasswordField id="signup-password" label="Password" value={signupForm.password} onChange={sf("password")} placeholder="Minimum 8 characters" error={errors.password} autoComplete="new-password" />
                    <div style={{ marginTop: 10 }}>
                      <PasswordStrength password={signupForm.password} />
                    </div>
                  </div>
                  <PasswordField id="confirm-password" label="Confirm Password" value={signupForm.confirmPassword} onChange={sf("confirmPassword")} placeholder="Repeat password" error={errors.confirmPassword} autoComplete="new-password" />
                </div>

                <div style={{ margin: "20px 0 24px" }}>
                  <Checkbox
                    id="agree-terms"
                    checked={agreed}
                    onChange={e => setAgreed(e.target.checked)}
                    label={<>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, fontWeight: 600 }}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, fontWeight: 600 }}>Privacy Policy</a></>}
                  />
                  <ValidationMessage>{errors.agreed}</ValidationMessage>
                </div>

                <AuthButton onClick={doSignup} loading={loading} disabled={loading}>
                  {loading ? "Creating account…" : "Create Account"}
                </AuthButton>

                <p style={{ textAlign: "center", fontSize: 13.5, color: C.textSub, marginTop: 22 }}>
                  Already have an account?{" "}
                  <button type="button" className="auth-link-btn" onClick={() => switchTo("login")}>Sign in</button>
                </p>
              </>
            )}

            {/* FORGOT PASSWORD */}
            {view === "forgot" && (
              <>
                <button type="button" className="auth-back-btn" onClick={() => switchTo("login")} style={{ marginBottom: 20 }}>
                  <span aria-hidden>‹</span> Back to Sign In
                </button>
                <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-.4px", marginBottom: 6 }}>Reset your password</h1>
                <p style={{ fontSize: 14, color: C.textSub, marginBottom: 24, lineHeight: 1.5 }}>Enter your email and we'll send you a link to reset it.</p>
                <TextField id="forgot-email" label="Email Address" type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="you@company.com" error={errors.forgot} autoComplete="email" />
                <AuthButton onClick={doForgot} loading={loading} disabled={loading} style={{ marginTop: 20 }}>
                  {loading ? "Sending…" : "Send Reset Link"}
                </AuthButton>
              </>
            )}

          </div>
          <p style={{ textAlign: "center", fontSize: 12, color: C.green, fontWeight: 700, marginTop: 24 }}>© 2026 SDR ReachOut. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
